import { Extent } from 'ol/extent'
import { useState } from 'react'
import ApiClient from '../../../api'
import ImportPoints from './import-points'
import {
  Gcps,
  ProjectFile,
  ProjectFileId,
  RegisterGcpsCallback,
  GcpState,
  Gcp as GcpFeature,
  Markers,
} from './types'
import RegisterGcps from './register-gcps'
import {
  FeatureCollection,
  featureCollection,
  Point,
  point,
} from '@turf/helpers'

type GcpProps = {
  gcpState?: GcpState
  bounds: Extent
  images: FeatureCollection<Point>
  files: Record<ProjectFileId, ProjectFile>
  apiClient: ApiClient
  mapboxToken: string
  onRegister: RegisterGcpsCallback
  onCancel: () => void
}

type GcpData = {
  sourceCrs: string
  gcps: Gcps
}

export default function Gcp({
  gcpState: initialState,
  bounds,
  images,
  files,
  mapboxToken,
  onRegister,
  onCancel,
}: GcpProps) {
  const [gcpData, setGcps] = useState<GcpData | undefined>(
    initialState
      ? {
          sourceCrs: initialState.crs,
          gcps: featureCollection(
            initialState.gcps.map(
              ({ id, coordinates, wgs84Coordinates }): GcpFeature =>
                point(wgs84Coordinates, {
                  id,
                  name: id,
                  numberMarkers: initialState.markers.filter(
                    ({ gcpId }) => gcpId === id
                  ).length,
                  sourceCoordinates: coordinates,
                })
            )
          ),
        }
      : undefined
  )
  const gcps = gcpData?.gcps
  const [markers] = useState<Markers | undefined>(buildMarkers)

  return !gcpData || !gcps ? (
    <ImportPoints
      bounds={bounds}
      onImport={(sourceCrs, gcps) => setGcps({ sourceCrs, gcps })}
      onCancel={onCancel}
    />
  ) : (
    <RegisterGcps
      sourceCrs={gcpData.sourceCrs}
      gcps={gcps}
      markers={markers}
      images={images}
      files={files}
      mapboxToken={mapboxToken}
      onRegister={onRegister}
      onCancel={onCancel}
    />
  )

  function buildMarkers() {
    if (initialState) {
      const markers: Markers = {}
      for (const marker of initialState.markers) {
        const gcpMarkers = (markers[marker.gcpId] ||= {})
        const imageCoordinates = marker.coordinates
        // Internally, markers use OpenLayers coordinates where Y is flipped,
        // but external state have "normal" Y coordinates where 0 is top and
        // grows downwards
        const coordinates = [imageCoordinates[0], -imageCoordinates[1]]
        gcpMarkers[marker.imageFilename] = point(coordinates, {
          include: true,
        })
      }

      return markers
    }

    return undefined
  }
}
